import { useEffect, useState } from "react";
import { api, type AdminAgent } from "./api.ts";

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "à l'instant";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return "à l'instant";
  const min = Math.floor(sec / 60);
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h} h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `il y a ${d} j`;
  return new Date(iso).toLocaleDateString("fr-FR");
}

export function Agents(props: { token: string }) {
  const [list, setList] = useState<AdminAgent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createdSip, setCreatedSip] = useState<{
    sipUser: string;
    password: string;
    wsServer: string;
    domain: string;
  } | null>(null);

  // New-agent form fields.
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newSipUser, setNewSipUser] = useState("");
  const [newLoginPwd, setNewLoginPwd] = useState("");

  async function refresh(): Promise<void> {
    try {
      const r = await api.listAgents(props.token);
      setList(r.agents);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.token]);

  async function create(): Promise<void> {
    setBusy(true);
    try {
      const r = await api.createAgent(props.token, {
        email: newEmail.trim(),
        name: newName.trim() || undefined,
        sipUser: newSipUser.trim(),
        loginPassword: newLoginPwd || undefined,
      });
      setCreatedSip({
        sipUser: r.sip.user,
        password: r.sip.password,
        wsServer: r.sip.wsServer,
        domain: r.sip.domain,
      });
      setCreateOpen(false);
      setNewEmail("");
      setNewName("");
      setNewSipUser("");
      setNewLoginPwd("");
      await refresh();
    } catch (err) {
      setError(`create: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function patch(id: string, input: Parameters<typeof api.patchAgent>[2]): Promise<void> {
    setBusy(true);
    try {
      await api.patchAgent(props.token, id, input);
      await refresh();
    } catch (err) {
      setError(`patch: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <section className="card">
        <div className="card-header">
          <h3>Agents</h3>
          <div className="actions">
            <button className="primary" onClick={() => setCreateOpen((v) => !v)} type="button">
              {createOpen ? "Annuler" : "+ Nouvel agent"}
            </button>
            <button className="ghost" onClick={() => void refresh()} type="button">↻ Actualiser</button>
          </div>
        </div>
        {error && <div className="error">{error}</div>}

        {createOpen && (
          <div className="dial" style={{ gap: "0.5rem", padding: "0.25rem" }}>
            <input
              placeholder="E-mail"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={busy}
            />
            <input
              placeholder="Nom (facultatif)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              disabled={busy}
            />
            <input
              placeholder="Identifiant SIP (minuscules, 3 à 32 caractères)"
              value={newSipUser}
              onChange={(e) => setNewSipUser(e.target.value.toLowerCase())}
              disabled={busy}
            />
            <input
              placeholder="Mot de passe (facultatif, ≥ 8 caractères)"
              type="password"
              value={newLoginPwd}
              onChange={(e) => setNewLoginPwd(e.target.value)}
              disabled={busy}
            />
            <button
              className="primary"
              type="button"
              onClick={() => void create()}
              disabled={busy || !newEmail.trim() || !newSipUser.trim()}
            >
              Créer
            </button>
          </div>
        )}

        {createdSip && (
          <div className="toast" style={{ marginTop: "0.5rem" }}>
            <strong>Identifiants SIP (visibles une seule fois) :</strong>
            <pre style={{ whiteSpace: "pre-wrap", fontSize: 12 }}>
{`Identifiant SIP : ${createdSip.sipUser}
Mot de passe    : ${createdSip.password}
WS              : ${createdSip.wsServer}
Domaine         : ${createdSip.domain}`}
            </pre>
            <button className="ghost" type="button" onClick={() => setCreatedSip(null)}>
              Fermer
            </button>
          </div>
        )}

        {list == null && !error && <div className="empty">Chargement…</div>}
        {list != null && list.length === 0 && <div className="empty">Aucun agent pour le moment.</div>}
        {list != null && list.length > 0 && (
          <table className="data">
            <thead>
              <tr>
                <th>E-mail</th>
                <th>Nom</th>
                <th>Identifiant SIP</th>
                <th>Rôle</th>
                <th>Activé</th>
                <th>Dernière connexion</th>
              </tr>
            </thead>
            <tbody>
              {list.map((a) => (
                <tr key={a.id}>
                  <td><code>{a.email}</code></td>
                  <td>{a.name ?? "—"}</td>
                  <td><code>{a.sipUser}</code></td>
                  <td>{a.role === "admin" ? "administrateur" : "agent"}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={a.enabled}
                      disabled={busy}
                      onChange={(e) => void patch(a.id, { enabled: e.target.checked })}
                    />
                  </td>
                  <td>
                    {a.lastSeenAt ? (
                      <span title={new Date(a.lastSeenAt).toLocaleString("fr-FR")}>
                        {formatRelative(a.lastSeenAt)}
                      </span>
                    ) : (
                      <span className="muted">jamais</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}
